<template>
  <div class="email-accounts-page">
    <div class="page-header">
      <div class="header-content">
        <h1>
          <i class="fas fa-envelope-open-text"></i>
          Comptes Email
        </h1>
        <p>Gérez vos comptes email et leurs configurations</p>
      </div>
      <div class="header-actions">
        <button 
          class="btn btn-primary"
          @click="showAddModal = true"
        >
          <i class="fas fa-plus"></i>
          Ajouter un compte
        </button>
      </div>
    </div>

    <div class="accounts-grid" v-if="accounts.length > 0">
      <div 
        v-for="account in accounts" 
        :key="account.id"
        class="account-card"
        :class="{ 'account-disabled': !account.enabled }"
      >
        <div class="account-header">
          <div class="account-info">
            <div class="account-avatar">
              <i :class="getProviderIcon(account.provider)"></i>
            </div>
            <div class="account-details">
              <h3>{{ account.email }}</h3>
              <span class="account-provider">{{ getProviderName(account.provider) }}</span>
            </div>
          </div>
          <div class="account-status">
            <span 
              class="status-badge"
              :class="getStatusClass(account.status)"
            >
              {{ getStatusText(account.status) }}
            </span>
          </div>
        </div>

        <div class="account-body">
          <div class="account-stats">
            <div class="stat-item">
              <i class="fas fa-inbox"></i>
              <span class="stat-label">Messages</span>
              <span class="stat-value">{{ account.messageCount || 0 }}</span>
            </div>
            <div class="stat-item">
              <i class="fas fa-clock"></i>
              <span class="stat-label">Dernière sync</span>
              <span class="stat-value">{{ formatDate(account.lastSync) }}</span>
            </div>
          </div>

          <div class="account-config">
            <div class="config-item">
              <span class="config-label">Serveur:</span>
              <span class="config-value">{{ account.host }}:{{ account.port }}</span>
            </div>
            <div class="config-item">
              <span class="config-label">Sécurité:</span>
              <span class="config-value">{{ account.security }}</span>
            </div>
            <div class="config-item" v-if="account.folder">
              <span class="config-label">Dossier:</span>
              <span class="config-value">{{ account.folder }}</span>
            </div>
          </div>
        </div>

        <div class="account-actions">
          <button 
            class="btn btn-sm btn-outline"
            @click="testConnection(account)"
            :disabled="testing === account.id"
          >
            <i class="fas fa-plug" v-if="testing !== account.id"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            Tester
          </button>
          <button 
            class="btn btn-sm btn-outline"
            @click="syncAccount(account)"
            :disabled="syncing === account.id"
          >
            <i class="fas fa-sync" v-if="syncing !== account.id"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            Synchroniser
          </button>
          <button 
            class="btn btn-sm btn-outline"
            @click="editAccount(account)"
          >
            <i class="fas fa-edit"></i>
            Modifier
          </button>
          <button 
            class="btn btn-sm btn-danger"
            @click="deleteAccount(account)"
          >
            <i class="fas fa-trash"></i>
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-envelope-open"></i>
      </div>
      <h3>Aucun compte email configuré</h3>
      <p>Ajoutez votre premier compte email pour commencer à traiter vos messages automatiquement.</p>
      <button 
        class="btn btn-primary"
        @click="showAddModal = true"
      >
        <i class="fas fa-plus"></i>
        Ajouter un compte
      </button>
    </div>

    <!-- Modal d'ajout/modification -->
    <div v-if="showAddModal || editingAccount" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>
            <i class="fas fa-envelope-plus"></i>
            {{ editingAccount ? 'Modifier le compte' : 'Ajouter un compte email' }}
          </h2>
          <button class="modal-close" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <form @submit.prevent="saveAccount" class="modal-body">
          <div class="form-section">
            <h3>Type de compte</h3>
            <div class="provider-selection">
              <label 
                v-for="provider in providers" 
                :key="provider.value"
                class="provider-option"
                :class="{ active: form.provider === provider.value }"
              >
                <input 
                  type="radio" 
                  v-model="form.provider" 
                  :value="provider.value"
                  @change="onProviderChange"
                >
                <div class="provider-card">
                  <i :class="provider.icon"></i>
                  <span>{{ provider.label }}</span>
                </div>
              </label>
            </div>
          </div>

          <div class="form-section">
            <h3>Configuration</h3>
            <div class="form-grid">
              <div class="form-group">
                <label for="email">Adresse email *</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': errors.email }"
                  required
                >
                <div v-if="errors.email" class="invalid-feedback">
                  {{ errors.email }}
                </div>
              </div>

              <div class="form-group" v-if="form.provider === 'imap'">
                <label for="password">Mot de passe *</label>
                <div class="password-input">
                  <input
                    id="password"
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password }"
                  >
                  <button
                    type="button"
                    class="password-toggle"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                </div>
                <div v-if="errors.password" class="invalid-feedback">
                  {{ errors.password }}
                </div>
              </div>

              <div class="form-group" v-if="form.provider === 'imap'">
                <label for="host">Serveur IMAP *</label>
                <input
                  id="host"
                  v-model="form.host"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.host }"
                  placeholder="imap.example.com"
                >
                <div v-if="errors.host" class="invalid-feedback">
                  {{ errors.host }}
                </div>
              </div>

              <div class="form-group" v-if="form.provider === 'imap'">
                <label for="port">Port</label>
                <input
                  id="port"
                  v-model="form.port"
                  type="number"
                  class="form-control"
                  placeholder="993"
                >
              </div>

              <div class="form-group" v-if="form.provider === 'imap'">
                <label for="security">Sécurité</label>
                <select id="security" v-model="form.security" class="form-control">
                  <option value="ssl">SSL/TLS</option>
                  <option value="starttls">STARTTLS</option>
                  <option value="none">Aucune</option>
                </select>
              </div>

              <div class="form-group">
                <label for="folder">Dossier à surveiller</label>
                <input
                  id="folder"
                  v-model="form.folder"
                  type="text"
                  class="form-control"
                  placeholder="INBOX"
                >
              </div>

              <div class="form-group full-width">
                <label class="checkbox-label">
                  <input v-model="form.enabled" type="checkbox">
                  <span class="checkmark"></span>
                  Activer ce compte
                </label>
              </div>
            </div>
          </div>

          <div class="oauth-section" v-if="form.provider !== 'imap'">
            <div class="oauth-info">
              <i class="fas fa-info-circle"></i>
              <div>
                <h4>Authentification OAuth2</h4>
                <p>Cliquez sur le bouton ci-dessous pour vous connecter avec {{ getProviderName(form.provider) }}.</p>
              </div>
            </div>
            <button 
              type="button"
              class="btn btn-oauth"
              @click="startOAuthFlow"
              :disabled="oauthLoading"
            >
              <i v-if="oauthLoading" class="fas fa-spinner fa-spin"></i>
              <i v-else :class="getProviderIcon(form.provider)"></i>
              {{ oauthLoading ? 'Connexion...' : `Se connecter avec ${getProviderName(form.provider)}` }}
            </button>
          </div>
        </form>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Annuler
          </button>
          <button 
            type="submit"
            class="btn btn-primary"
            @click="saveAccount"
            :disabled="saving"
          >
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useEmailAccountsStore } from '../stores/emailAccounts'

export default {
  name: 'EmailAccounts',
  setup() {
    const emailAccountsStore = useEmailAccountsStore()
    
    const showAddModal = ref(false)
    const editingAccount = ref(null)
    const showPassword = ref(false)
    const testing = ref(null)
    const syncing = ref(null)
    const saving = ref(false)
    const oauthLoading = ref(false)
    
    const form = reactive({
      email: '',
      password: '',
      provider: 'gmail',
      host: '',
      port: 993,
      security: 'ssl',
      folder: 'INBOX',
      enabled: true
    })
    
    const errors = reactive({})
    
    const providers = [
      { value: 'gmail', label: 'Gmail', icon: 'fab fa-google' },
      { value: 'outlook', label: 'Outlook', icon: 'fab fa-microsoft' },
      { value: 'imap', label: 'IMAP', icon: 'fas fa-server' }
    ]
    
    const accounts = computed(() => emailAccountsStore.accounts)
    
    const getProviderIcon = (provider) => {
      const providerData = providers.find(p => p.value === provider)
      return providerData ? providerData.icon : 'fas fa-envelope'
    }
    
    const getProviderName = (provider) => {
      const providerData = providers.find(p => p.value === provider)
      return providerData ? providerData.label : provider
    }
    
    const getStatusClass = (status) => {
      switch (status) {
        case 'connected': return 'status-success'
        case 'error': return 'status-error'
        case 'syncing': return 'status-warning'
        default: return 'status-inactive'
      }
    }
    
    const getStatusText = (status) => {
      switch (status) {
        case 'connected': return 'Connecté'
        case 'error': return 'Erreur'
        case 'syncing': return 'Synchronisation'
        default: return 'Inactif'
      }
    }
    
    const formatDate = (date) => {
      if (!date) return 'Jamais'
      return new Date(date).toLocaleString('fr-FR')
    }
    
    const resetForm = () => {
      Object.assign(form, {
        email: '',
        password: '',
        provider: 'gmail',
        host: '',
        port: 993,
        security: 'ssl',
        folder: 'INBOX',
        enabled: true
      })
      Object.keys(errors).forEach(key => delete errors[key])
    }
    
    const closeModal = () => {
      showAddModal.value = false
      editingAccount.value = null
      resetForm()
    }
    
    const onProviderChange = () => {
      if (form.provider === 'gmail') {
        form.host = 'imap.gmail.com'
        form.port = 993
        form.security = 'ssl'
      } else if (form.provider === 'outlook') {
        form.host = 'outlook.office365.com'
        form.port = 993
        form.security = 'ssl'
      }
    }
    
    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key])
      
      if (!form.email) {
        errors.email = 'L\'adresse email est requise'
      }
      
      if (form.provider === 'imap') {
        if (!form.password) {
          errors.password = 'Le mot de passe est requis'
        }
        if (!form.host) {
          errors.host = 'Le serveur IMAP est requis'
        }
      }
      
      return Object.keys(errors).length === 0
    }
    
    const startOAuthFlow = async () => {
      oauthLoading.value = true
      try {
        const result = await emailAccountsStore.startOAuthFlow(form.provider, form.email)
        if (result.success) {
          // Rediriger vers l'URL d'autorisation
          window.location.href = result.authUrl
        } else {
          errors.general = result.message
        }
      } catch (error) {
        console.error('Erreur OAuth:', error)
        errors.general = 'Erreur lors de l\'authentification OAuth'
      } finally {
        oauthLoading.value = false
      }
    }
    
    const saveAccount = async () => {
      if (!validateForm()) return
      
      saving.value = true
      try {
        const accountData = { ...form }
        
        let result
        if (editingAccount.value) {
          result = await emailAccountsStore.updateAccount(editingAccount.value.id, accountData)
        } else {
          result = await emailAccountsStore.createAccount(accountData)
        }
        
        if (result.success) {
          closeModal()
          await emailAccountsStore.loadAccounts()
        } else {
          errors.general = result.message
        }
      } catch (error) {
        console.error('Erreur sauvegarde:', error)
        errors.general = 'Erreur lors de la sauvegarde'
      } finally {
        saving.value = false
      }
    }
    
    const editAccount = (account) => {
      editingAccount.value = account
      Object.assign(form, account)
      showAddModal.value = true
    }
    
    const deleteAccount = async (account) => {
      if (confirm(`Êtes-vous sûr de vouloir supprimer le compte ${account.email} ?`)) {
        try {
          const result = await emailAccountsStore.deleteAccount(account.id)
          if (result.success) {
            await emailAccountsStore.loadAccounts()
          }
        } catch (error) {
          console.error('Erreur suppression:', error)
        }
      }
    }
    
    const testConnection = async (account) => {
      testing.value = account.id
      try {
        const result = await emailAccountsStore.testConnection(account.id)
        // Afficher le résultat du test
        alert(result.success ? 'Connexion réussie !' : `Erreur: ${result.message}`)
      } catch (error) {
        console.error('Erreur test:', error)
        alert('Erreur lors du test de connexion')
      } finally {
        testing.value = null
      }
    }
    
    const syncAccount = async (account) => {
      syncing.value = account.id
      try {
        const result = await emailAccountsStore.syncAccount(account.id)
        if (result.success) {
          await emailAccountsStore.loadAccounts()
        }
      } catch (error) {
        console.error('Erreur sync:', error)
      } finally {
        syncing.value = null
      }
    }
    
    onMounted(() => {
      emailAccountsStore.loadAccounts()
    })
    
    return {
      accounts,
      showAddModal,
      editingAccount,
      showPassword,
      testing,
      syncing,
      saving,
      oauthLoading,
      form,
      errors,
      providers,
      getProviderIcon,
      getProviderName,
      getStatusClass,
      getStatusText,
      formatDate,
      closeModal,
      onProviderChange,
      startOAuthFlow,
      saveAccount,
      editAccount,
      deleteAccount,
      testConnection,
      syncAccount
    }
  }
}
</script>

<style scoped>
.email-accounts-page {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-content h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.header-content h1 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.header-content p {
  color: #6b7280;
  margin: 0;
}

.accounts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.account-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.account-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.account-card.account-disabled {
  opacity: 0.6;
  background: #f9fafb;
}

.account-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.account-info {
  display: flex;
  align-items: center;
}

.account-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  margin-right: 1rem;
}

.account-details h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.account-provider {
  color: #6b7280;
  font-size: 0.875rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-success {
  background: #dcfce7;
  color: #166534;
}

.status-error {
  background: #fee2e2;
  color: #991b1b;
}

.status-warning {
  background: #fef3c7;
  color: #92400e;
}

.status-inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.account-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.stat-item i {
  color: #6366f1;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-weight: 600;
  color: #1f2937;
}

.account-config {
  margin-bottom: 1rem;
}

.config-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.config-item:last-child {
  border-bottom: none;
}

.config-label {
  font-weight: 500;
  color: #6b7280;
}

.config-value {
  color: #1f2937;
}

.account-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn i {
  margin-right: 0.5rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-primary {
  background: #6366f1;
  color: white;
}

.btn-primary:hover {
  background: #5b21b6;
}

.btn-outline {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-outline:hover {
  background: #f9fafb;
  color: #374151;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-oauth {
  background: #4285f4;
  color: white;
  width: 100%;
  justify-content: center;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: #9ca3af;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 2rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-header h2 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.form-section {
  margin-bottom: 2rem;
}

.form-section h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.provider-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.provider-option {
  cursor: pointer;
}

.provider-option input {
  display: none;
}

.provider-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.provider-option.active .provider-card {
  border-color: #6366f1;
  background: #f0f9ff;
}

.provider-card i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #6b7280;
}

.provider-option.active .provider-card i {
  color: #6366f1;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-control {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.password-input {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-label input {
  margin-right: 0.5rem;
}

.invalid-feedback {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.oauth-section {
  margin-top: 1rem;
}

.oauth-info {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  background: #f0f9ff;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.oauth-info i {
  color: #0ea5e9;
  margin-right: 0.75rem;
  margin-top: 0.25rem;
}

.oauth-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.oauth-info p {
  color: #6b7280;
  margin: 0;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .accounts-grid {
    grid-template-columns: 1fr;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .provider-selection {
    grid-template-columns: 1fr;
  }
  
  .account-actions {
    justify-content: center;
  }
}

.accounts-grid {
  grid-template-columns: repeat(auto-fill, minmax(min(400px, 100%), 1fr));
}

.account-card,
.account-info,
.account-details,
.header-content {
  min-width: 0;
}

.account-details h3,
.config-value {
  overflow-wrap: anywhere;
}

.config-item {
  gap: 1rem;
}

.config-value {
  text-align: right;
  min-width: 0;
}

.account-avatar {
  flex-shrink: 0;
}

@media (max-width: 991.98px) {
  .email-accounts-page {
    padding: 0;
  }

  .page-header {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 767.98px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
  }

  .header-content h1 {
    font-size: 1.6rem;
  }

  .header-actions .btn {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }

  .account-card {
    padding: 1.25rem;
  }

  .account-header {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  /* L'adresse prend toute la ligne, le statut passe dessous (aligné au texte) */
  .account-info {
    flex: 1 1 100%;
  }

  .account-details h3 {
    font-size: 1rem;
  }

  .account-status {
    margin-left: calc(40px + 0.75rem);
    margin-top: -0.25rem;
  }

  .account-avatar {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-right: 0.75rem;
  }

  .stat-item {
    min-width: 0;
    text-align: center;
  }

  .account-actions .btn {
    flex: 1 1 calc(50% - 0.25rem);
    justify-content: center;
    min-height: 40px;
  }

  /* Modale : plein écran sur téléphone, corps défilant */
  .modal-overlay {
    align-items: stretch;
  }

  .modal-content {
    width: 100%;
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    padding: 1rem 1.25rem;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }

  .modal-header h2 {
    font-size: 1.2rem;
    min-width: 0;
  }

  .modal-body {
    padding: 1.25rem;
  }

  .modal-footer {
    flex-wrap: wrap;
    padding: 1rem 1.25rem;
  }

  .modal-footer .btn {
    flex: 1 1 auto;
    justify-content: center;
    min-height: 44px;
  }
}

@media (pointer: coarse) {
  .modal-close,
  .password-toggle {
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .password-toggle {
    right: 0;
  }
}
</style>